import { useState, useEffect, useRef, useMemo } from 'react';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

const PRESET_TICKERS = ['SPX', 'SPY', 'NVDA', 'AAPL', 'MSFT', 'AMZN', 'GOOG', 'META', 'TSLA', 'WMT', 'MU', 'PLTR', 'AMD'];
const STORAGE_KEY = 'options-custom-tickers';

function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function calcDTE(date) {
  const msPerDay = 86400000;
  return Math.round((new Date(date + 'T00:00:00') - new Date(todayET() + 'T00:00:00')) / msPerDay);
}

export default function GenericOptionsWidget() {
  const [customTickers, setCustomTickers] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  });
  const allTickers = [...PRESET_TICKERS, ...customTickers.filter(t => !PRESET_TICKERS.includes(t))];

  const [symbol, setSymbol] = useState('SPX');
  const [selectedDate, setSelectedDate] = useState(todayET);
  const [contracts, setContracts] = useState(null);
  const [underlyingPrice, setUnderlyingPrice] = useState(null);
  const [sort, setSort] = useState({ col: 'volume', dir: 'desc' });
  const [connStatus, setConnStatus] = useState('connecting');

  // + button state
  const [addingTicker, setAddingTicker] = useState(false);
  const [newTickerInput, setNewTickerInput] = useState('');
  const addInputRef = useRef(null);

  // Focus the add-ticker input when it appears
  useEffect(() => {
    if (addingTicker && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [addingTicker]);

  const commitNewTicker = () => {
    const t = newTickerInput.trim().toUpperCase();
    if (t && !allTickers.includes(t)) {
      const updated = [...customTickers, t];
      setCustomTickers(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setSymbol(t);
    } else if (t && allTickers.includes(t)) {
      setSymbol(t);
    }
    setAddingTicker(false);
    setNewTickerInput('');
  };

  // Fetch/poll contracts for selected symbol + date
  useEffect(() => {
    let mounted = true;
    let pollTimer = null;

    setContracts(null);
    setConnStatus('connecting');

    const url = `${API_URL}/api/options?symbol=${symbol}&date=${selectedDate}`;

    async function pollOnce() {
      try {
        const token = await getValidToken();
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mounted) {
          setContracts(data.contracts);
          setUnderlyingPrice(data.spy_price);
          setConnStatus('live');
        }
      } catch {
        if (mounted) setConnStatus('reconnecting');
      }
    }

    // Poll every 5 s for today, 30 s for other dates
    const interval = selectedDate === todayET() ? 5000 : 30000;
    pollOnce();
    pollTimer = setInterval(pollOnce, interval);

    return () => {
      mounted = false;
      clearInterval(pollTimer);
    };
  }, [symbol, selectedDate]);

  const toggleSort = (col) =>
    setSort(s => s.col === col ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });

  const sorted = useMemo(() => {
    if (!contracts) return null;
    const key = { volume: 'volume', price: 'price', change_pct: 'change_pct', strike: 'strike' }[sort.col] || 'volume';
    return [...contracts].sort((a, b) =>
      sort.dir === 'desc' ? b[key] - a[key] : a[key] - b[key]
    );
  }, [contracts, sort]);

  const dotColor = connStatus === 'live' ? '#22c55e' : connStatus === 'reconnecting' ? '#f59e0b' : '#6b7280';
  const dotLabel = connStatus === 'live' ? 'Live' : connStatus === 'reconnecting' ? 'Reconnecting…' : 'Connecting…';

  const SortArrow = ({ col }) =>
    sort.col === col
      ? <span className="ml-1" style={{ color: '#60a5fa' }}>{sort.dir === 'desc' ? '↓' : '↑'}</span>
      : <span className="ml-1 text-gray-600">↕</span>;

  const dte = calcDTE(selectedDate);
  const dteColor = dte === 0 ? '#22c55e' : dte < 0 ? '#ef4444' : '#f59e0b';

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">

        {/* LEFT: ticker dropdown + add button */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={symbol}
              onChange={e => setSymbol(e.target.value)}
              className="appearance-none text-xs font-bold rounded px-3 py-1.5 pr-7 border cursor-pointer focus:outline-none focus:border-blue-500"
              style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
            >
              {allTickers.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {/* custom chevron */}
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
          </div>

          {addingTicker ? (
            <div className="flex items-center gap-1">
              <input
                ref={addInputRef}
                value={newTickerInput}
                onChange={e => setNewTickerInput(e.target.value.toUpperCase())}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitNewTicker();
                  if (e.key === 'Escape') { setAddingTicker(false); setNewTickerInput(''); }
                }}
                placeholder="TICKER"
                maxLength={6}
                className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500 w-20 uppercase"
                style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151' }}
              />
              <button
                onClick={commitNewTicker}
                className="text-xs rounded px-2 py-1 font-bold"
                style={{ backgroundColor: '#22c55e22', color: '#22c55e' }}
              >✓</button>
              <button
                onClick={() => { setAddingTicker(false); setNewTickerInput(''); }}
                className="text-xs rounded px-2 py-1"
                style={{ backgroundColor: '#374151', color: '#9ca3af' }}
              >✕</button>
            </div>
          ) : (
            <button
              onClick={() => setAddingTicker(true)}
              title="Add custom ticker"
              className="w-7 h-7 flex items-center justify-center rounded border text-sm font-bold transition-colors"
              style={{ backgroundColor: '#1f2937', color: '#60a5fa', borderColor: '#374151' }}
            >+</button>
          )}
        </div>

        {/* CENTER: date picker + DTE badge */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={e => e.target.value && setSelectedDate(e.target.value)}
            className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500"
            style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151' }}
          />
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded"
            style={{ backgroundColor: dteColor + '22', color: dteColor }}
          >
            {dte} DTE
          </span>
        </div>

        {/* RIGHT: underlying price + connection status */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold" style={{ color: '#f9fafb' }}>{symbol}</span>
            {underlyingPrice === null
              ? <span className="w-16 h-4 bg-gray-800 rounded animate-pulse inline-block align-middle" />
              : underlyingPrice > 0
                ? <span className="text-sm font-bold tabular-nums" style={{ color: '#60a5fa' }}>
                    ${underlyingPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                : <span className="text-sm text-gray-500">—</span>
            }
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} />
            <span className="text-xs text-gray-400">{dotLabel}</span>
          </div>
        </div>
      </div>

      {/* Loading skeleton */}
      {sorted === null && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-7 bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {sorted !== null && sorted.length === 0 && (() => {
        const d = new Date(selectedDate + 'T12:00:00');
        const dow = d.getDay();
        const isWeekend = dow === 0 || dow === 6;
        return (
          <div className="text-center py-8 text-gray-500 text-sm">
            {isWeekend
              ? `No contracts — ${d.toLocaleDateString('en-US', { weekday: 'long' })} is not a trading day`
              : `No contracts found for ${symbol} on this date`}
          </div>
        );
      })()}

      {/* Contracts table */}
      {sorted !== null && sorted.length > 0 && (
        <div className="overflow-auto" style={{ maxHeight: 400 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider w-16">Type</th>
                <th
                  className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('strike')}
                >Contract <SortArrow col="strike" /></th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('price')}
                >Price <SortArrow col="price" /></th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('change_pct')}
                >Chg% <SortArrow col="change_pct" /></th>
                <th
                  className="text-right pb-2 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('volume')}
                >Volume <SortArrow col="volume" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => {
                const isCall = c.type === 'C';
                const chgColor = c.change_pct >= 0 ? '#22c55e' : '#ef4444';
                const tvBase = c.symbol.slice(0, -9);
                const tvStrike = Number.isInteger(c.strike) ? c.strike.toFixed(1) : c.strike.toString();
                const tvLink = `https://www.tradingview.com/chart/GYtOhxAt/?symbol=OPRA%3A${tvBase}${c.type}${tvStrike}`;
                return (
                  <tr key={c.symbol} className="border-t border-gray-800 hover:bg-gray-800/40">
                    <td className="py-1.5 pr-3">
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: isCall ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isCall ? '#22c55e' : '#ef4444',
                        }}
                      >{isCall ? 'CALL' : 'PUT'}</span>
                    </td>
                    <td className="py-1.5 pr-3">
                      <a
                        href={tvLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs hover:text-blue-300"
                        style={{ color: '#60a5fa' }}
                      >${c.strike % 1 === 0 ? c.strike.toFixed(0) : c.strike.toFixed(1)}</a>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-200">${c.price.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color: chgColor }}>
                      {c.change_pct >= 0 ? '+' : ''}{c.change_pct.toFixed(1)}%
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-300">{c.volume.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
